import dayjs from 'dayjs';
import { EmblaOptionsType } from 'embla-carousel';
import ClassNames from 'embla-carousel-class-names';
import useEmblaCarousel from 'embla-carousel-react';
import Image from 'next/image';
import React, { useEffect, useRef, useState } from 'react';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';

import useInterval from '@hooks/useInterval';

import { DotButton, useDotButton } from './DotButton';

type CarouselPropsType = {
  slides: SlideType[];
  options?: EmblaOptionsType;
};

const AUTO_SCROLL_DELAY = 5; // sec
const EMBLA_OPTIONS: EmblaOptionsType = { loop: true, duration: 30 };

const Carousel: React.FC<CarouselPropsType> = ({ slides }) => {
  const videoElements = useRef([] as (HTMLVideoElement | null)[]);
  const [isShowingThumbnails, setShowingThumbnails] = useState<boolean>(false);
  const [isFullscreen, _setIsFullscreen] = useState<boolean>(false);
  const isFullscreenReference = useRef(isFullscreen);
  const setIsFullscreen = (newState: boolean) => {
    isFullscreenReference.current = newState;
    _setIsFullscreen(newState);
  };
  const [isMuted, setIsMuted] = useState<boolean>(true);
  const [slideInView, _setSlideInView] = useState<number>(0);
  const slideInViewReference = useRef(slideInView);
  const setSlideInView = (newState: number) => {
    slideInViewReference.current = newState;
    _setSlideInView(newState);
  };
  const [lastPlayingVideoIndex, setLastPlayingVideoIndex] = useState<number>();
  const [isVideoInView, setIsVideoInView] = useState<boolean>(false);
  const [emblaReference, emblaApi] = useEmblaCarousel(EMBLA_OPTIONS, [
    ClassNames(),
  ]);
  const { selectedIndex, scrollSnaps, onDotButtonClick } =
    useDotButton(emblaApi);
  const isVideo = /\.(mp4)|(mov)$/i;
  const [nextScrollTime, setNextScrollTime] = useState<Date>();
  const [isScrollingPaused, setScrollingPaused] = useState(false);
  const [isVideoPaused, setVideoPaused] = useState<boolean[]>([]);

  const muteAllVideos = () => {
    for (const video of videoElements.current) {
      if (typeof video?.muted === 'boolean') {
        video.muted = true;
      }
    }
    setIsMuted(true);
  };
  const hasMultipleSlides = slides.length > 1;

  useEffect(() => {
    if (!emblaApi || !slides || slides.length === 0) {
      return;
    }
    // TODO: check initial timeout
    setNextScrollTime(dayjs().add(AUTO_SCROLL_DELAY, 's').toDate());

    emblaApi.on('slidesInView', () => {
      if (isFullscreenReference.current) {
        return;
      }
      const slidesInView = emblaApi.slidesInView();
      if (!slidesInView || slidesInView.length === 0) {
        return;
      }
      // determines the currently coming or displayed single slide
      // starting over after the last slide is an edge case, then we take the index 0, otherwise the last item from the slide array
      const currentOrUpcomingSlideIndex =
        slidesInView[0] === 0 && slidesInView[1] !== 1
          ? 0
          : (slidesInView.at(-1) as number);
      // if slide didn't change (eg.: sliding completed or exit fullscreen), nothing to do
      if (slideInViewReference.current === currentOrUpcomingSlideIndex) {
        return;
      }
      // set current slide
      setSlideInView(currentOrUpcomingSlideIndex);
      muteAllVideos();
    });
  }, [emblaApi]);

  useEffect(() => {
    const video = videoElements.current[slideInView];

    if (!video) return;

    // Pause carousel scrolling when video starts
    const handlePlay = () => {
      setScrollingPaused(true);
    };

    // Resume scrolling and reset timer after video ends
    const handleEnded = () => {
      setScrollingPaused(false);
      setNextScrollTime(dayjs().add(AUTO_SCROLL_DELAY, 's').toDate());

      emblaApi?.scrollNext();
    };

    video.addEventListener('play', handlePlay);
    video.addEventListener('ended', handleEnded);

    return () => {
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('ended', handleEnded);
    };
  }, [slideInView]);
  useEffect(() => {
    if (!slides || slides.length === 0) {
      return;
    }
    if (lastPlayingVideoIndex !== slideInView) {
      const video = videoElements.current[slideInView];
      if (video) {
        video.pause();
        video.currentTime = 0;
      }
      setLastPlayingVideoIndex(undefined);
    }
  }, [slideInView, lastPlayingVideoIndex]);

  useEffect(() => {
    if (!slides || slides.length === 0) {
      return;
    }
    const isCurrentSlideVideo = isVideo.test(slides[slideInView].Url);
    setIsVideoInView(isCurrentSlideVideo);
    if (isFullscreen) {
      return;
    }
    if (isCurrentSlideVideo) {
      const currentVideo = videoElements.current[slideInView];
      if (currentVideo) {
        currentVideo.play();
        currentVideo.muted = isMuted; // TODO: check if works
        const remainingTime =
          currentVideo.duration - (currentVideo.currentTime ?? 0);
        setNextScrollTime(dayjs().add(remainingTime, 's').toDate());
        setLastPlayingVideoIndex(slideInView);
      }
    } else {
      setNextScrollTime(dayjs().add(AUTO_SCROLL_DELAY, 's').toDate());
    }
  }, [slideInView, isFullscreen]);
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        setScrollingPaused(true); // Pause auto-scroll
      } else {
        setScrollingPaused(false); // Resume auto-scroll
        setNextScrollTime(dayjs().add(AUTO_SCROLL_DELAY, 's').toDate()); // Reset timer
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () =>
      document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);
  useInterval(
    () => {
      if (dayjs().isAfter(nextScrollTime)) {
        setNextScrollTime(dayjs().add(AUTO_SCROLL_DELAY, 's').toDate());
        emblaApi?.scrollNext();
      }
    },
    isScrollingPaused || !slides || slides.length === 0 ? null : 300
  );

  if (!slides || slides.length === 0) {
    return <></>;
  }

  const onVideoClick = (index: number) => {
    const currentVideo = videoElements.current[slideInView];
    if (currentVideo) {
      if (currentVideo.paused) {
        currentVideo.play();
        setVideoPaused(previous => {
          previous[index] = false;
          return [...previous];
        });
      } else {
        currentVideo.pause();
        setVideoPaused(previous => {
          previous[index] = true;
          return [...previous];
        });
      }
    }
  };

  const showThumbnails = () => {
    setScrollingPaused(true);
    muteAllVideos();
    setShowingThumbnails(true);
  };

  const selectSlide = (slideIndex: number) => {
    setShowingThumbnails(false);
    setScrollingPaused(false);
    if (!emblaApi) {
      return;
    }
    emblaApi.scrollTo(slideIndex);
  };

  const enterFullscreen = () => {
    setIsFullscreen(true);
    setScrollingPaused(true);
    // TODO: clean up, likely unnecessary
    if (isVideoInView) {
      const currentVideo = videoElements.current[slideInView];
      if (currentVideo) {
        // currentVideo.play();
      }
    }
  };
  const exitFullscreen = () => {
    setIsFullscreen(false);
    setScrollingPaused(false);
  };

  const toggleMute = () => {
    if (!emblaApi) {
      return;
    }
    const slidesInView = emblaApi.slidesInView();
    // fullscreen workaround
    if (!slidesInView.includes(slideInView)) {
      slidesInView.push(slideInView);
    }
    for (const slideIndex of slidesInView) {
      const slideElement = videoElements.current[slideIndex];
      if (!!slideElement) {
        // toggle video mute
        const previousState = slideElement.muted;
        slideElement.muted = !previousState;
        setIsMuted(slideElement.muted);
      }
    }
  };

  const renderThumbnail = (slide: SlideType, index: number) => {
    // Determine the position classes based on the index
    const positionClasses = [
      'top left',
      'top center',
      'top right',
      'middle left',
      'middle center',
      'middle right',
      'bottom left',
      'bottom center',
      'bottom right',
    ];

    return (
      <div
        className={`thumbnail ${positionClasses[slide.Order]} ${
          slideInView === index ? 'selected' : ''
        }`}
        key={slide.Key}
        onClick={() => selectSlide(index)}
      >
        {isVideo.test(slide.Url) ? (
          <>
            <video
              className='thumbnail_video'
              src={slide.Url + '#t=0.1'}
              muted={true}
              playsInline={true}
              autoPlay={false}
            />
            <div className='playVideoIconWrapper'>
              <img
                className='playVideoIcon'
                src='/icons/play.svg'
                alt='Play video'
                title='Play video'
                width={64}
                height={64}
              />
            </div>
          </>
        ) : (
          <img src={slide.Url} alt={`Thumbnail #${slide.Order + 1}`} />
        )}
      </div>
    );
  };

  const renderSlide = (slide: SlideType, index: number) => (
    <div className='embla__slide' key={slide.Key}>
      <TransformWrapper
        centerOnInit={true}
        doubleClick={{
          excluded: [],
          step: 1,
          mode: 'toggle',
        }}
        initialScale={1}
        maxScale={1.62}
        minScale={1}
        wheel={{
          smoothStep: 0.01,
          step: 0.62,
        }}
      >
        <TransformComponent>
          {isVideo.test(slide.Url) ? (
            <>
              <video
                ref={element => {
                  videoElements.current[index] = element;
                }}
                src={slide.Url}
                className='embla__slide__video'
                autoPlay
                muted={isMuted}
                playsInline={true}
                loop={isFullscreen || !hasMultipleSlides}
                onClick={() => onVideoClick(index)}
              />
              {isVideoPaused[index] && (
                <div className='playVideoIconWrapper'>
                  <img
                    className='playVideoIcon'
                    src='/icons/play.svg'
                    alt='Play video'
                    title='Play video'
                    width={64}
                    height={64}
                  />
                </div>
              )}
            </>
          ) : (
            <img
              className='embla__slide__img'
              src={slide.Url}
              alt={`Artwork #${slide.Order + 1}`}
            />
          )}
        </TransformComponent>
      </TransformWrapper>
    </div>
  );

  return (
    <>
      <div className={`fullscreen ${isFullscreen ? 'fullscreen_active' : ''}`}>
        <button
          className='fullscreenButton exitFullscreenButton'
          onClick={() => exitFullscreen()}
        >
          <Image
            src={'/icons/fullscreenClose.svg'}
            alt={'Exit Fullscreen'}
            width={16}
            height={16}
          />
        </button>
        {isVideoInView && (
          <button
            className='fullscreenButton muteUnmuteButton'
            onClick={toggleMute}
          >
            <Image
              src={`/icons/${isMuted ? 'un' : ''}mute.svg`}
              alt={isMuted ? 'Unmute' : 'Mute'}
              width={16}
              height={16}
            />
          </button>
        )}
      </div>
      {/* TODO: background might not be needed for fullscreen */}
      <div
        className={`background ${isFullscreen ? 'fullscreen_active' : ''}`}
        onClick={exitFullscreen}
      ></div>
      <div
        className={`thumbnails ${
          isShowingThumbnails ? 'thumbnails_active' : ''
        }`}
      >
        {slides.map(renderThumbnail)}
      </div>
      <div
        className={`embla ${isFullscreen ? 'fullscreen_active' : ''} ${
          isShowingThumbnails ? 'thumbnails_active' : ''
        }`}
      >
        <div className='embla__viewport' ref={emblaReference}>
          <div className='embla__container'>{slides.map(renderSlide)}</div>
          <div className='carouselButtonsWrapper'>
            {hasMultipleSlides && (
              <>
                <button
                  className='carouselButton thumbnailButton'
                  onClick={showThumbnails}
                >
                  <Image
                    src={'/icons/thumbnails.svg'}
                    alt={'Show thumbnails'}
                    title='Thumbnails'
                    width={16}
                    height={16}
                  />
                </button>
                <button className='carouselButton slideCounter'>
                  <div className='slideCounterTextWrapper'>
                    <strong>{slideInView + 1} </strong> &nbsp;/ {slides.length}
                  </div>
                </button>
              </>
            )}
            <button
              className='carouselButton enterFullscreenButton'
              onClick={enterFullscreen}
            >
              <Image
                src={'/icons/fullscreenOpen.svg'}
                alt={'Enter Fullscreen'}
                title='Fullscreen'
                width={16}
                height={16}
              />
            </button>
            {isVideoInView && (
              <button
                className='carouselButton muteUnmuteButton'
                onClick={toggleMute}
              >
                <Image
                  src={`/icons/${isMuted ? 'un' : ''}mute.svg`}
                  alt={isMuted ? 'Unmute' : 'Mute'}
                  title={isMuted ? 'Unmute' : 'Mute'}
                  width={16}
                  height={16}
                />
              </button>
            )}
            {hasMultipleSlides && (
              <div className='embla__dots carouselButton'>
                {scrollSnaps.map((_, index) => (
                  <DotButton
                    key={index}
                    onClick={() => onDotButtonClick(index)}
                    className={'embla__dot'.concat(
                      index === selectedIndex ? ' embla__dot--selected' : ''
                    )}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Carousel;
